import React, { useState, useEffect, useCallback } from 'react';
import {
  ShieldCheck,
  FileText,
  ClipboardList,
  BarChart3,
  BookOpen,
  Upload,
  Loader2,
  Download,
  Trash2,
  CheckCircle2,
  AlertTriangle,
  Save,
  RefreshCw,
  FileCheck,
  Layers,
  Users2,
  Zap,
  Target,
  ListChecks,
  Plus,
  History,
  Eye,
  Lock,
  ArrowLeft,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../services/api';
import OrganisationTypePicker, { ENTITY_TYPES } from '../components/OrganisationTypePicker';
import AttendanceTab from './governance/AttendanceTab';
import ObserveTab from './governance/ObserveTab';
import MethodologyTab from './governance/MethodologyTab';
import PlanningTab from './governance/PlanningTab';
import ImprovementPlanTab from './governance/ImprovementPlanTab';

const MATURITY_LABELS = {
  0: 'Not Present',
  1: 'Ad Hoc',
  2: 'Basic',
  3: 'Established',
  4: 'Advanced',
  5: 'Leading Practice',
};

const RAG_STYLES = {
  red: { bg: '#ff5252', text: '#fff', label: 'Red' },
  amber: { bg: '#ffb300', text: '#000', label: 'Amber' },
  green: { bg: '#43a047', text: '#fff', label: 'Green' },
  blue: { bg: '#1e88e5', text: '#fff', label: 'Blue' },
};

const PRIORITY_STYLES = {
  High: 'bg-red-500 text-white',
  Medium: 'bg-[#ffb300] text-black',
  Low: 'bg-white text-black',
};

const STATUS_STYLES = {
  draft: 'bg-white text-black',
  in_progress: 'bg-[#ffb300] text-black',
  completed: 'bg-[#43a047] text-white',
};

const STATUS_LABELS = {
  draft: 'Draft',
  in_progress: 'In Progress',
  completed: 'Completed',
};

const TABS = [
  { id: 'overview', label: 'Overview', icon: BookOpen },
  { id: 'methodology', label: 'Methodology', icon: Layers },
  { id: 'planning', label: 'Planning', icon: ListChecks },
  { id: 'documents', label: 'Documents', icon: FileText },
  { id: 'attendance', label: 'Attendance', icon: Users2 },
  { id: 'observe', label: 'Observe', icon: Eye },
  { id: 'assessment', label: 'Assessment', icon: BarChart3 },
  { id: 'results', label: 'Results', icon: Target },
  { id: 'improvement', label: 'Improvement Plan', icon: Zap },
  { id: 'history', label: 'History', icon: History },
];

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch (e) {
    return iso;
  }
};

const RagBadge = ({ rag, children }) => {
  const s = RAG_STYLES[rag] || RAG_STYLES.amber;
  return (
    <span
      className="inline-flex items-center text-xs font-bold uppercase px-2 py-0.5 border border-ink/10 rounded-lg"
      style={{ backgroundColor: s.bg, color: s.text }}
    >
      {children || s.label}
    </span>
  );
};

const GovernanceReview = () => {
  const [loading, setLoading] = useState(true);
  const [orgType, setOrgType] = useState(null);
  const [pendingOrgType, setPendingOrgType] = useState('charity');
  const [savingOrgType, setSavingOrgType] = useState(false);

  const [reviewId, setReviewId] = useState(null);
  const [currentReviewId, setCurrentReviewId] = useState(null);
  const [reviewMeta, setReviewMeta] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [config, setConfig] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [documentTypes, setDocumentTypes] = useState([]);
  const [reviewDocumentTypes, setReviewDocumentTypes] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [answers, setAnswers] = useState({});
  const [progress, setProgress] = useState({ answered: 0, total: 0, percent: 0 });
  const [generatedReview, setGeneratedReview] = useState(null);
  const [sections, setSections] = useState({});

  const [activeTab, setActiveTab] = useState('overview');
  const [saving, setSaving] = useState(false);
  const [exportingAssessment, setExportingAssessment] = useState(false);
  const [importingAssessment, setImportingAssessment] = useState(false);
  const [savingSection, setSavingSection] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [uploadingType, setUploadingType] = useState(null);
  const [choosingEntity, setChoosingEntity] = useState(false);
  const [switchingReview, setSwitchingReview] = useState(false);

  const readOnly = reviewId !== null && currentReviewId !== null && reviewId !== currentReviewId;

  const refreshReviews = useCallback(async () => {
    try {
      const list = await api.getEntityReviews();
      setReviews(list.reviews || []);
      return list.reviews || [];
    } catch (e) {
      return [];
    }
  }, []);

  const loadReview = useCallback(async (rid) => {
    const data = await api.getEntityReview(rid);
    setReviewId(data.review.id);
    setReviewMeta(data.review);
    try {
      const cfg = await api.getEntityConfig(data.review.organisation_type);
      setConfig(cfg);
    } catch (e) {
      // keep previous config
    }
    setQuestions(data.questions || []);
    setDocumentTypes(data.document_types || []);
    setReviewDocumentTypes(data.review_document_types || []);
    setAnswers(data.answers || {});
    setSections(data.sections || {});
    try {
      const docList = await api.listEntityDocuments(rid);
      setDocuments(docList.documents || []);
    } catch (e) {
      setDocuments([]);
    }
    setProgress(data.progress || { answered: 0, total: 0, percent: 0 });
    if (data.review.has_generated_review) {
      try {
        const gen = await api.getEntityGeneratedReview(rid);
        setGeneratedReview(gen);
      } catch (e) {
        setGeneratedReview(null);
      }
    } else {
      setGeneratedReview(null);
    }
  }, []);

  const bootstrap = useCallback(async () => {
    setLoading(true);
    try {
      const ot = await api.getOrganisationType();
      const type = ot.organisation_type || null;
      setOrgType(type);
      setPendingOrgType(type || 'charity');
      if (!type) {
        setLoading(false);
        return;
      }

      const data = await api.getCurrentEntityReview();
      const rid = data.review.id;
      setCurrentReviewId(rid);
      await loadReview(rid);
      await refreshReviews();
    } catch (error) {
      toast.error(error.message || 'Failed to load governance review');
    } finally {
      setLoading(false);
    }
  }, [loadReview, refreshReviews]);

  useEffect(() => {
    bootstrap();
  }, [bootstrap]);

  const handleSetOrgType = async () => {
    try {
      setSavingOrgType(true);
      await api.setOrganisationType(pendingOrgType);
      toast.success('Organisation type set');
      setActiveTab('overview');
      await bootstrap();
      setChoosingEntity(false);
    } catch (error) {
      toast.error(error.message || 'Failed to set organisation type');
    } finally {
      setSavingOrgType(false);
    }
  };

  const handleOpenReview = async (rid) => {
    if (rid === reviewId) {
      setActiveTab('overview');
      return;
    }
    try {
      setSwitchingReview(true);
      await loadReview(rid);
      setActiveTab('overview');
    } catch (error) {
      toast.error(error.message || 'Failed to open review');
    } finally {
      setSwitchingReview(false);
    }
  };

  const handleReturnToCurrent = async () => {
    if (!currentReviewId || currentReviewId === reviewId) return;
    try {
      setSwitchingReview(true);
      await loadReview(currentReviewId);
      setActiveTab('overview');
    } catch (error) {
      toast.error(error.message || 'Failed to load current review');
    } finally {
      setSwitchingReview(false);
    }
  };

  const handleScore = (qid, score) => {
    if (readOnly) return;
    setAnswers((prev) => ({
      ...prev,
      [qid]: { ...(prev[qid] || {}), score },
    }));
  };

  const handleNote = (qid, note) => {
    if (readOnly) return;
    setAnswers((prev) => ({
      ...prev,
      [qid]: { ...(prev[qid] || {}), note },
    }));
  };

  const handleSaveAssessment = async () => {
    try {
      setSaving(true);
      const cleaned = {};
      Object.entries(answers).forEach(([qid, v]) => {
        if (v && v.score !== undefined && v.score !== null && v.score !== '') {
          cleaned[qid] = { score: Number(v.score), note: v.note || '' };
        }
      });
      const res = await api.saveEntityAssessment(reviewId, cleaned);
      setProgress(res.progress || progress);
      await refreshReviews();
      toast.success('Assessment saved');
    } catch (error) {
      toast.error(error.message || 'Failed to save assessment');
    } finally {
      setSaving(false);
    }
  };

  const handleExportAssessment = async () => {
    try {
      setExportingAssessment(true);
      await api.exportAssessment(reviewId);
      toast.success('Questionnaire downloaded');
    } catch (error) {
      toast.error(error.message || 'Failed to export questionnaire');
    } finally {
      setExportingAssessment(false);
    }
  };

  const handleImportAssessment = async (file) => {
    if (!file || readOnly) return;
    try {
      setImportingAssessment(true);
      const res = await api.importAssessment(reviewId, file);
      setAnswers(res.answers || {});
      setProgress(res.progress || progress);
      await refreshReviews();
      const msg = `Imported ${res.imported} answers (${res.scored} scored)`;
      if ((res.warnings || []).length > 0) {
        toast.warning(`${msg} — ${res.warnings.length} row(s) had issues`, {
          description: res.warnings.slice(0, 3).join(' '),
        });
      } else {
        toast.success(msg);
      }
    } catch (error) {
      toast.error(error.message || 'Failed to import questionnaire');
    } finally {
      setImportingAssessment(false);
    }
  };

  const handleSaveSection = async (partial) => {
    try {
      setSavingSection(true);
      const res = await api.saveEntitySections(reviewId, partial);
      setSections(res.sections || {});
      toast.success('Saved');
    } catch (error) {
      toast.error(error.message || 'Failed to save');
    } finally {
      setSavingSection(false);
    }
  };

  const handleUpload = async (documentType, file) => {
    if (!file || readOnly) return;
    try {
      setUploadingType(documentType);
      await api.uploadEntityDocument(reviewId, documentType, file);
      const list = await api.listEntityDocuments(reviewId);
      setDocuments(list.documents || []);
      toast.success('Document uploaded and analysed');
    } catch (error) {
      toast.error(error.message || 'Failed to upload document');
    } finally {
      setUploadingType(null);
    }
  };

  const handleDeleteDocument = async (docId) => {
    if (readOnly) return;
    try {
      await api.deleteEntityDocument(docId);
      setDocuments((prev) => prev.filter((d) => d.id !== docId));
      toast.success('Document removed');
    } catch (error) {
      toast.error(error.message || 'Failed to remove document');
    }
  };

  const handleGenerate = async () => {
    if (readOnly) return;
    try {
      setGenerating(true);
      const res = await api.generateEntityReview(reviewId);
      setGeneratedReview(res);
      await refreshReviews();
      toast.success('Review generated');
      setActiveTab('results');
    } catch (error) {
      toast.error(error.message || 'Failed to generate review');
    } finally {
      setGenerating(false);
    }
  };

  const handleDownload = async () => {
    try {
      setDownloading(true);
      const blob = await api.downloadEntityReport(reviewId);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Governance_Review_Report_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Report downloaded');
    } catch (error) {
      toast.error(error.message || 'Failed to download report');
    } finally {
      setDownloading(false);
    }
  };

  if (loading) {
    return (
      <div className="p-4 sm:p-6 lg:p-8 flex items-center justify-center min-h-[400px]">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="animate-spin" size={32} />
          <p className="font-bold uppercase tracking-wide">Loading governance review...</p>
        </div>
      </div>
    );
  }

  // First-run, or explicitly starting a review for a different entity
  if (!orgType || choosingEntity) {
    return (
      <div className="p-4 sm:p-6 lg:p-8 max-w-4xl mx-auto">
        <div className="mb-6">
          <h1 className="font-bold tracking-tight text-4xl mb-2 flex items-center gap-3">
            <ShieldCheck size={36} />
            {orgType ? 'New Entity Review' : 'Entity Governance Review'}
          </h1>
          <p className="font-medium text-ink/70">
            Choose an organisation type to unlock a tailored reference library, document checklist and maturity assessment. Each entity type keeps its own separate review.
          </p>
        </div>
        <div className="bg-white border border-ink/10 rounded-lg shadow-saas p-6">
          <h2 className="font-bold tracking-tight text-2xl mb-4">Select Organisation Type</h2>
          <OrganisationTypePicker value={pendingOrgType} onChange={setPendingOrgType} columns={4} />
          <div className="mt-6 flex flex-wrap gap-3">
            <button
              onClick={handleSetOrgType}
              disabled={savingOrgType}
              className="inline-flex items-center px-6 py-3 bg-ink text-white border border-ink/10 rounded-lg shadow-saas font-bold uppercase tracking-wide disabled:opacity-60"
            >
              {savingOrgType ? <Loader2 className="animate-spin mr-2" size={16} /> : <ShieldCheck size={16} className="mr-2" />}
              {savingOrgType ? 'Setting up...' : orgType ? 'Open Entity Review' : 'Start Entity Review'}
            </button>
            {orgType && (
              <button
                onClick={() => {
                  setPendingOrgType(orgType);
                  setChoosingEntity(false);
                }}
                disabled={savingOrgType}
                className="inline-flex items-center px-6 py-3 bg-white border border-ink/10 rounded-lg shadow-saas font-bold uppercase tracking-wide disabled:opacity-60"
              >
                <ArrowLeft size={16} className="mr-2" />
                Back to current review
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  const activeOrgLabel = ENTITY_TYPES.find((t) => t.value === orgType)?.label || orgType;
  const orgLabel = reviewMeta?.organisation_label || activeOrgLabel;

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-5xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h1 className="font-bold tracking-tight text-4xl mb-2 flex items-center gap-3">
          <ShieldCheck size={36} />
          Governance Review
        </h1>
        <div className="flex flex-wrap items-center gap-3">
          <span className="bg-lime border border-ink/10 rounded-lg px-3 py-1 font-bold uppercase text-sm tracking-wide">
            {orgLabel}
          </span>
          {reviewMeta?.name && (
            <span className="bg-white border border-ink/10 rounded-lg px-3 py-1 font-bold text-sm">
              {reviewMeta.name}
            </span>
          )}
          {readOnly ? (
            <span className="inline-flex items-center gap-1 bg-ink text-white border border-ink/10 rounded-lg px-3 py-1 font-bold uppercase text-xs tracking-wide">
              <Lock size={12} /> Read-only
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 bg-[#43a047] text-white border border-ink/10 rounded-lg px-3 py-1 font-bold uppercase text-xs tracking-wide">
              <CheckCircle2 size={12} /> Current
            </span>
          )}
          {!readOnly && (
            <button
              onClick={() => {
                setPendingOrgType(orgType);
                setChoosingEntity(true);
              }}
              className="inline-flex items-center gap-2 bg-ink text-white border border-ink/10 rounded-lg px-3 py-1 font-bold uppercase text-sm tracking-wide"
            >
              <Plus size={16} />
              New / switch entity review
            </button>
          )}
          <span className="text-sm font-bold uppercase tracking-wide text-ink/60">
            {progress.answered}/{progress.total} questions · {documents.length} document(s)
          </span>
        </div>
      </div>

      {/* Read-only banner */}
      {readOnly && (
        <div className="bg-ink text-white border border-ink/10 rounded-lg shadow-saas p-4 mb-6 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2 font-bold uppercase text-sm tracking-wide">
            <Lock size={16} />
            Viewing a past review — changes are disabled. You can still download its PDF report.
          </div>
          <button
            onClick={handleReturnToCurrent}
            disabled={switchingReview}
            className="inline-flex items-center px-4 py-2 bg-lime text-black border border-ink/10 rounded-lg font-bold uppercase text-sm tracking-wide disabled:opacity-60"
          >
            {switchingReview ? <Loader2 className="animate-spin mr-2" size={14} /> : <ArrowLeft size={14} className="mr-2" />}
            Return to current
          </button>
        </div>
      )}

      {/* Tabs */}
      <div className="flex flex-wrap gap-2 mb-6">
        {TABS.map((tab) => {
          const Icon = tab.icon;
          const active = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`inline-flex items-center gap-2 px-4 py-2 border border-ink/10 rounded-lg font-bold uppercase text-sm tracking-wide transition-all ${
                active ? 'bg-lime shadow-saas-sm' : 'bg-white'
              }`}
            >
              <Icon size={16} />
              {tab.label}
            </button>
          );
        })}
      </div>

      {activeTab === 'overview' && (
        <OverviewTab
          config={config}
          orgLabel={orgLabel}
          progress={progress}
          documents={documents}
          generatedReview={generatedReview}
          onGenerate={handleGenerate}
          generating={generating}
          onGoTo={setActiveTab}
          readOnly={readOnly}
          documentTypes={documentTypes}
          onUpload={handleUpload}
          uploadingType={uploadingType}
          onDeleteDocument={handleDeleteDocument}
        />
      )}

      {activeTab === 'methodology' && <MethodologyTab />}

      {activeTab === 'planning' && (
        <PlanningTab sections={sections} onSave={handleSaveSection} saving={savingSection} />
      )}

      {activeTab === 'documents' && (
        <ReviewDocumentsTab
          reviewDocumentTypes={reviewDocumentTypes}
          documents={documents}
          onUpload={handleUpload}
          uploadingType={uploadingType}
          onDelete={handleDeleteDocument}
          readOnly={readOnly}
        />
      )}

      {activeTab === 'attendance' && (
        <AttendanceTab reviewId={reviewId} readOnly={readOnly} />
      )}

      {activeTab === 'observe' && (
        <ObserveTab reviewId={reviewId} readOnly={readOnly} />
      )}

      {activeTab === 'improvement' && (
        <ImprovementPlanTab
          sections={sections}
          onSave={handleSaveSection}
          saving={savingSection}
          generatedReview={generatedReview}
          onGoTo={setActiveTab}
        />
      )}

      {activeTab === 'assessment' && (
        <AssessmentTab
          questions={questions}
          answers={answers}
          documents={documents}
          onScore={handleScore}
          onNote={handleNote}
          onSave={handleSaveAssessment}
          saving={saving}
          progress={progress}
          readOnly={readOnly}
          onExport={handleExportAssessment}
          exporting={exportingAssessment}
          onImport={handleImportAssessment}
          importing={importingAssessment}
        />
      )}

      {activeTab === 'results' && (
        <ResultsTab
          generatedReview={generatedReview}
          onGenerate={handleGenerate}
          generating={generating}
          onDownload={handleDownload}
          downloading={downloading}
          sections={sections}
          onSaveSection={handleSaveSection}
          savingSection={savingSection}
          readOnly={readOnly}
        />
      )}

      {activeTab === 'history' && (
        <HistoryTab
          reviews={reviews}
          currentReviewId={currentReviewId}
          activeReviewId={reviewId}
          onOpen={handleOpenReview}
          switching={switchingReview}
        />
      )}
    </div>
  );
};

const SectionCard = ({ title, icon: Icon, children, action }) => (
  <div className="bg-white border border-ink/10 rounded-lg shadow-saas mb-6">
    <div className="p-5 border-b border-ink/10 flex items-center justify-between gap-3">
      <h2 className="font-bold tracking-tight text-xl flex items-center gap-2">
        {Icon && <Icon size={20} />}
        {title}
      </h2>
      {action}
    </div>
    <div className="p-5">{children}</div>
  </div>
);

const OverviewTab = ({ config, orgLabel, progress, documents, generatedReview, onGenerate, generating, onGoTo, readOnly, documentTypes, onUpload, uploadingType, onDeleteDocument }) => (
  <div>
    <div className="grid sm:grid-cols-3 gap-4 mb-6">
      <div className="bg-white border border-ink/10 rounded-lg shadow-saas-sm p-5">
        <div className="text-sm font-bold uppercase tracking-wide text-ink/60 mb-1">Assessment</div>
        <div className="font-bold tracking-tight text-3xl">{progress.percent}%</div>
        <div className="text-xs font-bold uppercase text-ink/50 mt-1">{progress.answered}/{progress.total} answered</div>
      </div>
      <div className="bg-white border border-ink/10 rounded-lg shadow-saas-sm p-5">
        <div className="text-sm font-bold uppercase tracking-wide text-ink/60 mb-1">Documents</div>
        <div className="font-bold tracking-tight text-3xl">{documents.length}</div>
        <div className="text-xs font-bold uppercase text-ink/50 mt-1">uploaded & analysed</div>
      </div>
      <div className="bg-white border border-ink/10 rounded-lg shadow-saas-sm p-5">
        <div className="text-sm font-bold uppercase tracking-wide text-ink/60 mb-1">Overall Maturity</div>
        <div className="font-bold tracking-tight text-3xl">
          {generatedReview ? generatedReview.overall.score : '—'}
        </div>
        <div className="text-xs font-bold uppercase text-ink/50 mt-1">
          {generatedReview ? generatedReview.overall.maturity_level : 'not generated'}
        </div>
      </div>
    </div>

    <SectionCard
      title="Generate Review"
      icon={BarChart3}
      action={
        !readOnly && (
          <button
            onClick={onGenerate}
            disabled={generating}
            className="inline-flex items-center px-4 py-2 bg-lime border border-ink/10 rounded-lg shadow-saas-sm font-bold uppercase text-sm tracking-wide disabled:opacity-60"
          >
            {generating ? <Loader2 className="animate-spin mr-2" size={14} /> : <RefreshCw size={14} className="mr-2" />}
            {generating ? 'Generating...' : 'Generate'}
          </button>
        )
      }
    >
      <p className="font-medium text-ink/70">
        {readOnly
          ? 'This is a past review. Open the '
          : `Run the deterministic analysis engine across your ${orgLabel} maturity scores and uploaded documents to produce findings, recommendations and a 90-day improvement plan. Then view the `}
        <button className="font-bold underline" onClick={() => onGoTo('results')}>Results</button>
        {readOnly ? ' tab to view findings or download the PDF report.' : ' tab or download the PDF report.'}
      </p>
    </SectionCard>

    <SectionCard title={`Reference Library — ${orgLabel}`} icon={BookOpen}>
      <p className="font-medium text-ink/70 mb-4">
        Your assessment and recommendations are anchored to the following curated reference sources.
      </p>
      <div className="space-y-3">
        {(config?.references || []).map((ref, idx) => (
          <div key={ref.reference_id || idx} className="bg-cream border border-ink/10 rounded-lg p-3">
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-bold">{ref.reference_name}</span>
              {ref.reference_type && (
                <span className="text-xs font-bold uppercase px-2 py-0.5 bg-white border border-ink/10 rounded-lg">{ref.reference_type}</span>
              )}
            </div>
            {ref.section_or_principle && (
              <div className="text-xs font-bold uppercase tracking-wide text-ink/50 mt-0.5">{ref.section_or_principle}</div>
            )}
            {ref.summary && <p className="text-sm font-medium text-ink/70 mt-1">{ref.summary}</p>}
          </div>
        ))}
      </div>

      <div className="mt-6 pt-5 border-t border-ink/10">
        <h3 className="font-bold tracking-tight text-lg mb-1">Constitutional Documents</h3>
        <p className="font-medium text-ink/70 mb-4">
          {readOnly
            ? 'This is a past review. Documents are shown read-only and cannot be added or removed.'
            : 'Upload your governance documents against each expected type. Supported: PDF, DOCX, TXT, CSV, XLSX. Each upload is scanned for expected governance provisions.'}
        </p>
        <ConstitutionalDocuments
          documentTypes={documentTypes}
          documents={documents}
          onUpload={onUpload}
          uploadingType={uploadingType}
          onDelete={onDeleteDocument}
          readOnly={readOnly}
        />
      </div>
    </SectionCard>
  </div>
);

const ConstitutionalDocuments = ({ documentTypes, documents, onUpload, uploadingType, onDelete, readOnly }) => {
  const docsByType = (type) => documents.filter((d) => d.document_type === type);

  return (
    <div>
      <div className="space-y-4">
        {documentTypes.map((dt) => {
          const typeDocs = docsByType(dt);
          const isUploading = uploadingType === dt;
          return (
            <div key={dt} className="bg-cream border border-ink/10 rounded-lg p-5">
              <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                <h3 className="font-bold tracking-tight text-lg flex items-center gap-2">
                  <FileText size={18} />
                  {dt}
                </h3>
                {!readOnly && (
                  <label className="inline-flex items-center px-4 py-2 bg-lime border border-ink/10 rounded-lg shadow-saas-sm font-bold uppercase text-sm tracking-wide cursor-pointer disabled:opacity-60">
                    {isUploading ? <Loader2 className="animate-spin mr-2" size={14} /> : <Upload size={14} className="mr-2" />}
                    {isUploading ? 'Uploading...' : 'Upload'}
                    <input
                      type="file"
                      className="hidden"
                      accept=".pdf,.docx,.txt,.csv,.xlsx"
                      disabled={isUploading}
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        onUpload(dt, file);
                        e.target.value = '';
                      }}
                    />
                  </label>
                )}
              </div>

              {typeDocs.length === 0 ? (
                <p className="text-sm font-bold uppercase tracking-wide text-ink/40">No document uploaded</p>
              ) : (
                <div className="space-y-3">
                  {typeDocs.map((doc) => (
                    <div key={doc.id} className="bg-white border border-ink/10 rounded-lg p-3">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex items-center gap-2">
                          <FileCheck size={16} />
                          <span className="font-bold break-all">{doc.filename}</span>
                          {doc.extraction_status && doc.extraction_status !== 'ok' && (
                            <span className="text-xs font-bold uppercase text-red-500">
                              {doc.extraction_status}
                            </span>
                          )}
                        </div>
                        {!readOnly && (
                          <button
                            onClick={() => onDelete(doc.id)}
                            className="p-1.5 bg-red-500 text-white border border-ink/10 rounded-lg flex-shrink-0"
                            aria-label="Delete document"
                          >
                            <Trash2 size={14} />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const ReviewDocumentsTab = ({ reviewDocumentTypes, documents, onUpload, uploadingType, onDelete, readOnly }) => {
  const docsByType = (type) => documents.filter((d) => d.document_type === type);

  return (
    <div>
      <SectionCard title="In-Year Review Documents" icon={FileText}>
        <p className="font-medium text-ink/70">
          {readOnly
            ? 'This is a past review. Documents are shown read-only and cannot be added or removed.'
            : 'Upload the documents analysed as part of the review during the year — two sets of board and committee minutes, two sets of board and committee agendas, your annual board and committee cycle, and terms of office. Supported: PDF, DOCX, TXT, CSV, XLSX.'}
        </p>
      </SectionCard>

      <div className="space-y-4">
        {reviewDocumentTypes.map((rdt) => {
          const name = rdt.name || rdt;
          const expected = rdt.expected || 1;
          const typeDocs = docsByType(name);
          const isUploading = uploadingType === name;
          return (
            <div key={name} className="bg-white border border-ink/10 rounded-lg shadow-saas-sm p-5">
              <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                <h3 className="font-bold tracking-tight text-lg flex items-center gap-2">
                  <FileText size={18} />
                  {name}
                  {expected > 1 && (
                    <span className="text-xs font-bold uppercase px-2 py-0.5 bg-cream border border-ink/10 rounded-lg">
                      Upload {expected}
                    </span>
                  )}
                </h3>
                <div className="flex items-center gap-3">
                  <span className={`text-xs font-bold uppercase tracking-wide ${typeDocs.length >= expected ? 'text-green-600' : 'text-ink/40'}`}>
                    {typeDocs.length}/{expected} uploaded
                  </span>
                  {!readOnly && (
                    <label className="inline-flex items-center px-4 py-2 bg-lime border border-ink/10 rounded-lg shadow-saas-sm font-bold uppercase text-sm tracking-wide cursor-pointer disabled:opacity-60">
                      {isUploading ? <Loader2 className="animate-spin mr-2" size={14} /> : <Upload size={14} className="mr-2" />}
                      {isUploading ? 'Uploading...' : 'Upload'}
                      <input
                        type="file"
                        className="hidden"
                        accept=".pdf,.docx,.txt,.csv,.xlsx"
                        disabled={isUploading}
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          onUpload(name, file);
                          e.target.value = '';
                        }}
                      />
                    </label>
                  )}
                </div>
              </div>

              {typeDocs.length === 0 ? (
                <p className="text-sm font-bold uppercase tracking-wide text-ink/40">No document uploaded</p>
              ) : (
                <div className="space-y-3">
                  {typeDocs.map((doc) => (
                    <div key={doc.id} className="bg-cream border border-ink/10 rounded-lg p-3">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex items-center gap-2">
                          <FileCheck size={16} />
                          <span className="font-bold break-all">{doc.filename}</span>
                          {doc.extraction_status && doc.extraction_status !== 'ok' && (
                            <span className="text-xs font-bold uppercase text-red-500">
                              {doc.extraction_status}
                            </span>
                          )}
                        </div>
                        {!readOnly && (
                          <button
                            onClick={() => onDelete(doc.id)}
                            className="p-1.5 bg-red-500 text-white border border-ink/10 rounded-lg flex-shrink-0"
                            aria-label="Delete document"
                          >
                            <Trash2 size={14} />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}

        {(() => {
          const otherDocs = docsByType('Other');
          const isUploading = uploadingType === 'Other';
          return (
            <div className="bg-white border border-ink/10 rounded-lg shadow-saas-sm p-5">
              <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                <div>
                  <h3 className="font-bold tracking-tight text-lg flex items-center gap-2">
                    <FileText size={18} />
                    Other Documents
                  </h3>
                  <p className="text-sm font-medium text-ink/60 mt-1">
                    Add any other documents you find relevant to the review — policies, reports, correspondence or anything else the board considers useful.
                  </p>
                </div>
                {!readOnly && (
                  <label className="inline-flex items-center px-4 py-2 bg-lime border border-ink/10 rounded-lg shadow-saas-sm font-bold uppercase text-sm tracking-wide cursor-pointer disabled:opacity-60">
                    {isUploading ? <Loader2 className="animate-spin mr-2" size={14} /> : <Upload size={14} className="mr-2" />}
                    {isUploading ? 'Uploading...' : 'Upload'}
                    <input
                      type="file"
                      className="hidden"
                      accept=".pdf,.docx,.txt,.csv,.xlsx"
                      disabled={isUploading}
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        onUpload('Other', file);
                        e.target.value = '';
                      }}
                    />
                  </label>
                )}
              </div>

              {otherDocs.length === 0 ? (
                <p className="text-sm font-bold uppercase tracking-wide text-ink/40">No document uploaded</p>
              ) : (
                <div className="space-y-3">
                  {otherDocs.map((doc) => (
                    <div key={doc.id} className="bg-cream border border-ink/10 rounded-lg p-3">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex items-center gap-2">
                          <FileCheck size={16} />
                          <span className="font-bold break-all">{doc.filename}</span>
                          {doc.extraction_status && doc.extraction_status !== 'ok' && (
                            <span className="text-xs font-bold uppercase text-red-500">
                              {doc.extraction_status}
                            </span>
                          )}
                        </div>
                        {!readOnly && (
                          <button
                            onClick={() => onDelete(doc.id)}
                            className="p-1.5 bg-red-500 text-white border border-ink/10 rounded-lg flex-shrink-0"
                            aria-label="Delete document"
                          >
                            <Trash2 size={14} />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })()}
      </div>
    </div>
  );
};

const AssessmentTab = ({ questions, answers, documents, onScore, onNote, onSave, saving, progress, readOnly, onExport, exporting, onImport, importing }) => (
  <div>
    <div className="bg-white border border-ink/10 rounded-lg shadow-saas mb-6 p-5 flex flex-wrap items-center justify-between gap-3 sticky top-0 z-10">
      <div>
        <div className="text-sm font-bold uppercase tracking-wide text-ink/60">Progress</div>
        <div className="font-bold tracking-tight text-2xl">{progress.answered}/{progress.total} answered ({progress.percent}%)</div>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <button
          onClick={onExport}
          disabled={exporting}
          className="inline-flex items-center px-4 py-3 bg-white border border-ink/10 rounded-lg shadow-saas-sm font-bold uppercase text-sm tracking-wide disabled:opacity-60"
        >
          {exporting ? <Loader2 className="animate-spin mr-2" size={14} /> : <Download size={14} className="mr-2" />}
          Export questionnaire
        </button>
        {!readOnly && (
          <label className="inline-flex items-center px-4 py-3 bg-lime border border-ink/10 rounded-lg shadow-saas-sm font-bold uppercase text-sm tracking-wide cursor-pointer">
            {importing ? <Loader2 className="animate-spin mr-2" size={14} /> : <Upload size={14} className="mr-2" />}
            {importing ? 'Importing...' : 'Import completed'}
            <input
              type="file"
              className="hidden"
              accept=".xlsx,.csv"
              disabled={importing}
              onChange={(e) => {
                onImport(e.target.files?.[0]);
                e.target.value = '';
              }}
            />
          </label>
        )}
        {!readOnly && (
          <button
            onClick={onSave}
            disabled={saving}
            className="inline-flex items-center px-6 py-3 bg-ink text-white border border-ink/10 rounded-lg shadow-saas font-bold uppercase tracking-wide disabled:opacity-60"
          >
            {saving ? <Loader2 className="animate-spin mr-2" size={16} /> : <Save size={16} className="mr-2" />}
            {saving ? 'Saving...' : 'Save Assessment'}
          </button>
        )}
      </div>
    </div>
    <p className="text-sm font-medium text-ink/60 mb-6 -mt-3">
      Completing the questionnaire offline? Export it as an Excel file, fill in the scores and notes, then import the completed file — answers update automatically.
    </p>

    <div className="space-y-5">
      {questions.map((q, idx) => {
        const current = answers[q.question_id] || {};
        const refLabel = [q.reference_name, q.reference_section].filter(Boolean).join(' · ');
        return (
          <div key={q.question_id} className="bg-white border border-ink/10 rounded-lg shadow-saas-sm p-5">
            <div className="flex items-start gap-3 mb-2">
              <span className="bg-lime border border-ink/10 rounded-lg px-2 py-0.5 font-bold text-sm flex-shrink-0">
                {idx + 1}
              </span>
              <div className="flex-1">
                <div className="text-xs font-bold uppercase tracking-wide text-ink/50">{q.pillar_name}</div>
                <h3 className="font-bold tracking-tight text-lg leading-snug">{q.text}</h3>
              </div>
            </div>

            {q.guidance && <p className="text-sm font-medium text-ink/70 mb-2">{q.guidance}</p>}
            {refLabel && (
              <div className="text-xs font-bold uppercase tracking-wide text-ink/50 mb-3 flex items-center gap-1">
                <BookOpen size={12} /> {refLabel}
              </div>
            )}

            <div className="flex flex-wrap gap-2 mb-3">
              {[0, 1, 2, 3, 4, 5].map((score) => {
                const selected = current.score === score;
                return (
                  <button
                    key={score}
                    onClick={() => onScore(q.question_id, score)}
                    disabled={readOnly}
                    className={`flex flex-col items-center px-3 py-2 border border-ink/10 rounded-lg font-bold transition-all ${
                      selected ? 'bg-lime shadow-saas-sm' : 'bg-white'
                    } ${readOnly ? (selected ? '' : 'opacity-50') + ' cursor-default' : 'hover:translate-x-0.5 hover:translate-y-0.5'}`}
                  >
                    <span className="text-lg leading-none">{score}</span>
                    <span className="text-[10px] uppercase tracking-wide mt-1 text-ink/60">{MATURITY_LABELS[score]}</span>
                  </button>
                );
              })}
            </div>

            {readOnly ? (
              current.note ? (
                <div className="w-full px-3 py-2 bg-cream border border-ink/10 rounded-lg font-medium text-sm whitespace-pre-wrap">
                  {current.note}
                </div>
              ) : null
            ) : (
              <textarea
                placeholder="Evidence / notes (optional)"
                value={current.note || ''}
                onChange={(e) => onNote(q.question_id, e.target.value)}
                rows={2}
                className="w-full px-3 py-2 bg-cream border border-ink/10 rounded-lg font-medium text-sm focus:outline-none focus:bg-lime/20 placeholder:text-ink/40"
              />
            )}
          </div>
        );
      })}
    </div>

    {!readOnly && (
      <div className="mt-6 flex justify-end">
        <button
          onClick={onSave}
          disabled={saving}
          className="inline-flex items-center px-6 py-3 bg-ink text-white border border-ink/10 rounded-lg shadow-saas font-bold uppercase tracking-wide disabled:opacity-60"
        >
          {saving ? <Loader2 className="animate-spin mr-2" size={16} /> : <Save size={16} className="mr-2" />}
          {saving ? 'Saving...' : 'Save Assessment'}
        </button>
      </div>
    )}
  </div>
);

const ResultsTab = ({
  generatedReview,
  onGenerate,
  generating,
  onDownload,
  downloading,
  sections,
  onSaveSection,
  savingSection,
  readOnly,
}) => {
  const savedSummary = sections?.executive_summary || '';
  const [summary, setSummary] = useState(savedSummary);
  useEffect(() => {
    setSummary(savedSummary);
  }, [savedSummary]);
  const summaryDirty = summary !== savedSummary;

  const execEditor = readOnly
    ? savedSummary
      ? (
        <SectionCard title="Reviewer's Executive Summary" icon={BookOpen}>
          <p className="font-medium text-ink/80 leading-relaxed whitespace-pre-wrap">{savedSummary}</p>
        </SectionCard>
      )
      : null
    : (
      <SectionCard
        title="Reviewer's Executive Summary"
        icon={BookOpen}
        action={
          <button
            onClick={() => onSaveSection({ executive_summary: summary })}
            disabled={savingSection || !summaryDirty}
            className="inline-flex items-center px-4 py-2 bg-ink text-white border border-ink/10 rounded-lg shadow-saas-sm font-bold uppercase text-sm tracking-wide disabled:opacity-50"
          >
            {savingSection ? <Loader2 className="animate-spin mr-2" size={14} /> : <Save size={14} className="mr-2" />}
            Save
          </button>
        }
      >
        <p className="text-sm font-medium text-ink/60 mb-2">
          Write your own narrative summary of the review. This appears at the top of the PDF report.
        </p>
        <textarea
          rows={6}
          value={summary}
          onChange={(e) => setSummary(e.target.value)}
          placeholder="Summarise the overall governance position, key strengths, priority risks and the headline recommendations..."
          className="w-full px-3 py-2 bg-cream border border-ink/10 rounded-lg font-medium text-sm focus:outline-none focus:bg-lime/20 placeholder:text-ink/40"
        />
      </SectionCard>
    );

  if (!generatedReview) {
    return (
      <div>
        {execEditor}
        <div className="bg-white border border-ink/10 rounded-lg shadow-saas p-8 text-center">
          <AlertTriangle size={36} className="mx-auto mb-3" />
          <h2 className="font-bold tracking-tight text-2xl mb-2">No review generated yet</h2>
          <p className="font-medium text-ink/70 mb-6 max-w-md mx-auto">
            {readOnly
              ? 'This past review was never generated, so there are no findings to display.'
              : 'Complete your assessment and upload documents, then generate the review to see findings and recommendations.'}
          </p>
          {!readOnly && (
            <button
              onClick={onGenerate}
              disabled={generating}
              className="inline-flex items-center px-6 py-3 bg-ink text-white border border-ink/10 rounded-lg shadow-saas font-bold uppercase tracking-wide disabled:opacity-60"
            >
              {generating ? <Loader2 className="animate-spin mr-2" size={16} /> : <RefreshCw size={16} className="mr-2" />}
              {generating ? 'Generating...' : 'Generate Review'}
            </button>
          )}
        </div>
      </div>
    );
  }

  const r = generatedReview;
  const overall = r.overall || {};

  return (
    <div>
      {execEditor}
      {/* Action bar */}
      <div className="bg-white border border-ink/10 rounded-lg shadow-saas mb-6 p-5 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div>
            <div className="text-sm font-bold uppercase tracking-wide text-ink/60">Overall Maturity</div>
            <div className="font-bold tracking-tight text-3xl flex items-center gap-3">
              {overall.score} / 5
              <RagBadge rag={overall.rag}>{overall.rag_meaning || overall.rag}</RagBadge>
            </div>
            <div className="text-xs font-bold uppercase text-ink/50 mt-1">
              {overall.maturity_level} · {overall.percent}%
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          {!readOnly && (
            <button
              onClick={onGenerate}
              disabled={generating}
              className="inline-flex items-center px-4 py-2 bg-white border border-ink/10 rounded-lg shadow-saas-sm font-bold uppercase text-sm tracking-wide disabled:opacity-60"
            >
              {generating ? <Loader2 className="animate-spin mr-2" size={14} /> : <RefreshCw size={14} className="mr-2" />}
              Regenerate
            </button>
          )}
          <button
            onClick={onDownload}
            disabled={downloading}
            className="inline-flex items-center px-4 py-2 bg-lime border border-ink/10 rounded-lg shadow-saas-sm font-bold uppercase text-sm tracking-wide disabled:opacity-60"
          >
            {downloading ? <Loader2 className="animate-spin mr-2" size={14} /> : <Download size={14} className="mr-2" />}
            {downloading ? 'Preparing...' : 'Download PDF'}
          </button>
        </div>
      </div>

      {/* Generated executive summary */}
      {r.executive_summary && (
        <SectionCard title="Generated Executive Summary" icon={BookOpen}>
          <p className="font-medium text-ink/80 leading-relaxed">{r.executive_summary}</p>
        </SectionCard>
      )}

      {/* Pillar scores */}
      {r.pillar_findings && r.pillar_findings.length > 0 && (
        <SectionCard title="Pillar Maturity" icon={BarChart3}>
          <div className="space-y-3">
            {r.pillar_findings.map((p) => (
              <div key={p.pillar_id} className="bg-cream border border-ink/10 rounded-lg p-3">
                <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
                  <span className="font-bold">{p.pillar_name}</span>
                  <div className="flex items-center gap-2">
                    <RagBadge rag={p.rag} />
                    {p.priority && (
                      <span className={`text-xs font-bold uppercase px-2 py-0.5 border border-ink/10 rounded-lg ${PRIORITY_STYLES[p.priority] || ''}`}>
                        {p.priority}
                      </span>
                    )}
                    <span className="font-bold tracking-tight text-lg">{p.score === null ? '—' : `${p.score}/5`}</span>
                  </div>
                </div>
                <div className="h-3 bg-white border border-ink/10 rounded-lg overflow-hidden">
                  <div className="h-full" style={{ width: `${((p.score || 0) / 5) * 100}%`, backgroundColor: RAG_STYLES[p.rag]?.bg || '#ffb300' }} />
                </div>
                <p className="text-xs font-bold uppercase tracking-wide text-ink/50 mt-2">{p.maturity}</p>
              </div>
            ))}
          </div>
        </SectionCard>
      )}

      {r.strengths && r.strengths.length > 0 && (
        <SectionCard title="Strengths" icon={CheckCircle2}>
          <ul className="space-y-2">
            {r.strengths.map((s, i) => (
              <li key={i} className="flex items-start gap-2 font-medium">
                <CheckCircle2 size={16} className="mt-0.5 flex-shrink-0" />
                <span>
                  {s.title}
                  {s.pillar_name && <span className="text-ink/50"> — {s.pillar_name} ({s.maturity})</span>}
                </span>
              </li>
            ))}
          </ul>
        </SectionCard>
      )}

      {r.key_risks && r.key_risks.length > 0 && (
        <SectionCard title="Key Risks" icon={AlertTriangle}>
          <div className="space-y-3">
            {r.key_risks.map((risk, i) => (
              <div key={i} className="bg-cream border border-ink/10 rounded-lg p-3">
                <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
                  <span className="font-bold flex items-center gap-2">
                    <AlertTriangle size={16} className="flex-shrink-0" />
                    {risk.title}
                  </span>
                  <div className="flex items-center gap-2">
                    <RagBadge rag={risk.rag} />
                    {risk.priority && (
                      <span className={`text-xs font-bold uppercase px-2 py-0.5 border border-ink/10 rounded-lg ${PRIORITY_STYLES[risk.priority] || ''}`}>
                        {risk.priority}
                      </span>
                    )}
                  </div>
                </div>
                {risk.finding && <p className="text-sm font-medium text-ink/70">{risk.finding}</p>}
                {risk.pillar_name && (
                  <p className="text-xs font-bold uppercase tracking-wide text-ink/50 mt-1">{risk.pillar_name}</p>
                )}
              </div>
            ))}
          </div>
        </SectionCard>
      )}

      {r.recommendations && r.recommendations.length > 0 && (
        <SectionCard title="Recommendations" icon={ClipboardList}>
          <div className="space-y-3">
            {r.recommendations.map((rec, i) => (
              <div key={i} className="bg-cream border border-ink/10 rounded-lg p-3 flex items-start gap-3">
                {rec.priority && (
                  <span className={`text-xs font-bold uppercase px-2 py-0.5 border border-ink/10 rounded-lg flex-shrink-0 ${PRIORITY_STYLES[rec.priority] || ''}`}>
                    {rec.priority}
                  </span>
                )}
                <div>
                  <span className="font-medium">{rec.text}</span>
                  {(rec.pillar_name || rec.reference_name) && (
                    <p className="text-xs font-bold uppercase tracking-wide text-ink/50 mt-1">
                      {rec.pillar_name}{rec.reference_name ? ` · ${rec.reference_name}` : ''}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </SectionCard>
      )}

      {r.plan_90day && r.plan_90day.length > 0 && (
        <SectionCard title="90-Day Improvement Plan" icon={ClipboardList}>
          <div className="space-y-3">
            {r.plan_90day.map((item, i) => (
              <div key={i} className="bg-cream border border-ink/10 rounded-lg p-3 flex items-start gap-3">
                {item.timeline && (
                  <span className="text-xs font-bold uppercase px-2 py-0.5 border border-ink/10 rounded-lg bg-white flex-shrink-0">
                    {item.timeline}
                  </span>
                )}
                <div>
                  <span className="font-medium">{item.action}</span>
                  <p className="text-xs font-bold uppercase tracking-wide text-ink/50 mt-1">
                    {item.owner}{item.pillar_name ? ` · ${item.pillar_name}` : ''}
                  </p>
                  {item.expected_outcome && (
                    <p className="text-xs font-medium text-ink/60 mt-1">{item.expected_outcome}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </SectionCard>
      )}

      {r.document_review && (
        <SectionCard title="Document Review" icon={FileText}>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <div className="text-xs font-bold uppercase tracking-wide text-ink/50 mb-1">
                Provisions Present ({(r.document_review.present_overall || []).length})
              </div>
              <div className="flex flex-wrap gap-1">
                {(r.document_review.present_overall || []).map((p) => (
                  <span key={p} className="text-xs font-bold px-2 py-0.5 border border-ink/10 rounded-lg" style={{ backgroundColor: '#43a047', color: '#fff' }}>
                    {p}
                  </span>
                ))}
              </div>
            </div>
            <div>
              <div className="text-xs font-bold uppercase tracking-wide text-ink/50 mb-1">
                Provisions Missing ({(r.document_review.missing_overall || []).length})
              </div>
              <div className="flex flex-wrap gap-1">
                {(r.document_review.missing_overall || []).map((m) => (
                  <span key={m} className="text-xs font-bold px-2 py-0.5 border border-ink/10 rounded-lg" style={{ backgroundColor: '#ff5252', color: '#fff' }}>
                    {m}
                  </span>
                ))}
                {(r.document_review.missing_overall || []).length === 0 && (
                  <span className="text-xs font-medium text-ink/40">None</span>
                )}
              </div>
            </div>
            {(r.document_review.weak_overall || []).length > 0 && (
              <div>
                <div className="text-xs font-bold uppercase tracking-wide text-ink/50 mb-1">
                  Weak / Limited ({(r.document_review.weak_overall || []).length})
                </div>
                <div className="flex flex-wrap gap-1">
                  {(r.document_review.weak_overall || []).map((w) => (
                    <span key={w} className="text-xs font-bold px-2 py-0.5 border border-ink/10 rounded-lg" style={{ backgroundColor: '#ffb300', color: '#000' }}>
                      {w}
                    </span>
                  ))}
                </div>
              </div>
            )}
            {(r.document_review.inconsistent_overall || []).length > 0 && (
              <div className="sm:col-span-2">
                <div className="text-xs font-bold uppercase tracking-wide text-ink/50 mb-1">
                  Inconsistent Provisions ({(r.document_review.inconsistent_overall || []).length})
                </div>
                <ul className="space-y-1">
                  {(r.document_review.inconsistent_overall || []).map((inc, i) => (
                    <li key={i} className="text-xs font-medium border border-ink/10 rounded-lg px-2 py-1" style={{ backgroundColor: '#fff3e0' }}>
                      {inc}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </SectionCard>
      )}

      {r.disclaimer && (
        <div className="bg-cream border border-ink/10 rounded-lg p-4 text-xs font-medium text-ink/60">
          {r.disclaimer}
        </div>
      )}
    </div>
  );
};

const HistoryTab = ({ reviews, currentReviewId, activeReviewId, onOpen, switching }) => {
  if (!reviews || reviews.length === 0) {
    return (
      <div className="bg-white border border-ink/10 rounded-lg shadow-saas p-8 text-center">
        <History size={36} className="mx-auto mb-3" />
        <h2 className="font-bold tracking-tight text-2xl mb-2">No reviews yet</h2>
        <p className="font-medium text-ink/70 max-w-md mx-auto">
          As you create reviews for different organisation types, they will appear here so you can revisit and compare them.
        </p>
      </div>
    );
  }

  return (
    <div>
      <SectionCard title="Review History" icon={History}>
        <p className="font-medium text-ink/70">
          Every governance review you have run, newest first. Open any past review to view it read-only and re-download its
          PDF report. The review marked <span className="font-bold">Current</span> is the active one for your selected
          organisation type.
        </p>
      </SectionCard>

      <div className="space-y-3">
        {reviews.map((rev) => {
          const isCurrent = rev.id === currentReviewId;
          const isViewing = rev.id === activeReviewId;
          const maturity = rev.maturity;
          return (
            <div
              key={rev.id}
              className={`bg-white border border-ink/10 rounded-lg p-4 flex flex-wrap items-center justify-between gap-3 ${
                isViewing ? 'shadow-saas-sm border-[3px]' : 'shadow-saas-sm'
              }`}
            >
              <div className="flex-1 min-w-[220px]">
                <div className="flex flex-wrap items-center gap-2 mb-1">
                  <span className="font-bold text-lg">{rev.name}</span>
                  <span className="bg-lime border border-ink/10 rounded-lg px-2 py-0.5 font-bold uppercase text-xs tracking-wide">
                    {rev.organisation_label}
                  </span>
                  {isCurrent && (
                    <span className="inline-flex items-center gap-1 bg-[#43a047] text-white border border-ink/10 rounded-lg px-2 py-0.5 font-bold uppercase text-xs tracking-wide">
                      <CheckCircle2 size={11} /> Current
                    </span>
                  )}
                  {isViewing && !isCurrent && (
                    <span className="inline-flex items-center gap-1 bg-ink text-white border border-ink/10 rounded-lg px-2 py-0.5 font-bold uppercase text-xs tracking-wide">
                      <Eye size={11} /> Viewing
                    </span>
                  )}
                </div>
                <div className="flex flex-wrap items-center gap-3 text-xs font-bold uppercase tracking-wide text-ink/50">
                  <span>Created {formatDate(rev.created_at)}</span>
                  {rev.generated_at && <span>· Generated {formatDate(rev.generated_at)}</span>}
                  <span
                    className={`px-2 py-0.5 border border-ink/10 rounded-lg ${STATUS_STYLES[rev.status] || 'bg-white text-black'}`}
                  >
                    {STATUS_LABELS[rev.status] || rev.status}
                  </span>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <div className="text-right">
                  <div className="text-xs font-bold uppercase tracking-wide text-ink/50">Maturity</div>
                  {maturity ? (
                    <div className="flex items-center gap-2 justify-end">
                      <span className="font-bold tracking-tight text-xl">{maturity.score}/5</span>
                      <RagBadge rag={maturity.rag} />
                    </div>
                  ) : (
                    <div className="font-bold tracking-tight text-xl text-ink/40">—</div>
                  )}
                  {maturity?.maturity_level && (
                    <div className="text-[10px] font-bold uppercase tracking-wide text-ink/50">
                      {maturity.maturity_level}
                    </div>
                  )}
                </div>

                <button
                  onClick={() => onOpen(rev.id)}
                  disabled={switching || isViewing}
                  className={`inline-flex items-center px-4 py-2 border border-ink/10 rounded-lg font-bold uppercase text-sm tracking-wide disabled:opacity-60 ${
                    isViewing ? 'bg-white' : 'bg-ink text-white shadow-saas-sm'
                  }`}
                >
                  {switching ? (
                    <Loader2 className="animate-spin mr-2" size={14} />
                  ) : (
                    <Eye size={14} className="mr-2" />
                  )}
                  {isViewing ? 'Open' : 'View'}
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default GovernanceReview;
